/**
 * One flag per top-level mission (a work card with nothing above it).
 * Horizontal reorder stores these mission ids and this pass puts them back
 * in that order after layout, including when a campaign is a single chain.
 */

import { HORIZONTAL_GAP, getNodeDimensions } from './constants';
import { buildDependencyGraph } from './graphBuilder';
import { getMilestoneAnchor, isMilestoneNode } from '../utils/milestoneUtils';
import { getNoteAnchor, isNoteNode, isWorkNode } from '../utils/noteUtils';

function readPos(positions, id) {
  if (!positions || !id) return null;
  const pos = typeof positions.get === 'function' ? positions.get(id) : positions[id];
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return null;
  return pos;
}

export function findTopLevelMissions(tasks, graph) {
  return (tasks || []).filter((task) => {
    if (!isWorkNode(task)) return false;
    const parents = graph?.parents?.get(task.id) || [];
    return parents.length === 0;
  });
}

function sortRootsByX(roots, positions) {
  return [...roots].sort((a, b) => {
    const dx = readPos(positions, a.id).x - readPos(positions, b.id).x;
    if (Math.abs(dx) > 0.5) return dx;
    return (a.id || '').localeCompare(b.id || '');
  });
}

/**
 * Each node belongs to the leftmost root that can reach it.
 * Notes and milestones follow their host.
 */
export function claimNodesByRoot(tasks, graph, roots) {
  const claimed = new Map();
  const rootIds = new Set(roots.map((root) => root.id));
  roots.forEach((root) => {
    const stack = [root.id];
    while (stack.length) {
      const id = stack.pop();
      if (!id || claimed.has(id)) continue;
      // A back-edge must not swallow the next top-level mission into this one.
      if (id !== root.id && rootIds.has(id)) continue;
      claimed.set(id, root.id);
      const children = graph.allChildren.get(id) || [];
      children.forEach((childId) => {
        if (!claimed.has(childId) && !rootIds.has(childId)) stack.push(childId);
      });
    }
  });

  (tasks || []).forEach((task) => {
    if (!task?.id || claimed.has(task.id)) return;
    const hostId = isNoteNode(task)
      ? getNoteAnchor(task)?.hostTaskId
      : getMilestoneAnchor(task)?.hostTaskId;
    if (hostId && claimed.has(hostId)) claimed.set(task.id, claimed.get(hostId));
  });

  return claimed;
}

/**
 * Flag placement for every top-level mission that currently has a coordinate.
 * `taskIds` is the group that should travel with that mission when it is reordered.
 * Milestones are omitted; the swap adds them from their host.
 */
export function buildTopLevelOrigins(tasks, positions, nodeDimensions = {}) {
  if (!tasks?.length || !positions) return [];

  const graph = buildDependencyGraph(tasks);
  const roots = findTopLevelMissions(tasks, graph).filter((root) => readPos(positions, root.id));
  if (roots.length === 0) return [];

  const ordered = sortRootsByX(roots, positions);
  const claimed = claimNodesByRoot(tasks, graph, ordered);
  const taskIdsByRoot = new Map(ordered.map((root) => [root.id, []]));
  const taskById = new Map(tasks.map((task) => [task.id, task]));

  claimed.forEach((owner, id) => {
    const task = taskById.get(id);
    if (!task || isMilestoneNode(task)) return;
    if (!readPos(positions, id)) return;
    const list = taskIdsByRoot.get(owner);
    if (list) list.push(id);
  });

  return ordered.map((root, index) => {
    const pos = readPos(positions, root.id);
    const dims = getNodeDimensions(root.id, nodeDimensions);
    const taskIds = taskIdsByRoot.get(root.id) || [];
    if (!taskIds.includes(root.id)) taskIds.unshift(root.id);
    return {
      id: root.id,
      taskIds,
      position: { x: pos.x, y: pos.y },
      width: dims.width,
      effectiveOrder: index,
      campaignId: root.sourceCampaign?.id,
      campaignColor: root.sourceCampaign?.color,
    };
  });
}

function desiredRootOrder(roots, positions, chainOrder) {
  const orderMap = new Map((chainOrder || []).map((id, index) => [id, index]));
  return [...roots].sort((a, b) => {
    const oa = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
    const ob = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
    if (oa !== ob) return oa - ob;
    const dx = readPos(positions, a.id).x - readPos(positions, b.id).x;
    if (Math.abs(dx) > 0.5) return dx;
    return (a.id || '').localeCompare(b.id || '');
  });
}

/**
 * Lay top-level missions out in the stored horizontal order.
 * Missions that have never been reordered keep their current left-to-right place
 * after the ones that have an order. No-op when the row is already in that order.
 */
export function applyStoredRootOrder(positions, tasks, nodeDimensions, chainOrder) {
  if (!positions || !chainOrder || chainOrder.length < 2 || !tasks?.length) return;

  const graph = buildDependencyGraph(tasks);
  const roots = findTopLevelMissions(tasks, graph).filter((root) => readPos(positions, root.id));
  if (roots.length < 2) return;

  const orderMap = new Map(chainOrder.map((id, index) => [id, index]));
  const known = roots.filter((root) => orderMap.has(root.id));
  if (known.length < 2) return;

  const visual = sortRootsByX(roots, positions);
  const desired = desiredRootOrder(roots, positions, chainOrder);
  if (desired.every((root, index) => root.id === visual[index].id)) return;

  const claimed = claimNodesByRoot(tasks, graph, visual);
  let cursor = Math.min(...roots.map((root) => readPos(positions, root.id).x));

  desired.forEach((root) => {
    const current = readPos(positions, root.id);
    const dx = cursor - current.x;
    if (Math.abs(dx) > 0.5) {
      claimed.forEach((owner, id) => {
        if (owner !== root.id) return;
        const pos = readPos(positions, id);
        if (!pos) return;
        positions[id] = { x: pos.x + dx, y: pos.y };
      });
    }
    const placed = readPos(positions, root.id);
    const width = getNodeDimensions(root.id, nodeDimensions).width;
    cursor = placed.x + width + HORIZONTAL_GAP;
  });
}
